import axios from 'axios';
import { store } from '~/store';
import { refreshTokenSuccess, logout } from '~/store/ducks/auth';
import { appendQueryParamsTo } from './urlutils';

// Em desenvolvimento, usa URL relativa para que o proxy intercepte
// O proxy redireciona /api-picktow para https://api.picktow.com/api/v1
const getApiBaseURL = () => {
  const apiURL = process.env.REACT_APP_API || '';
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

  // Se estiver em produção, sempre usa a URL completa
  if (isProduction) {
    return apiURL;
  }

  // Se estiver em desenvolvimento e a URL for da API picktow, usa URL relativa
  if (apiURL.includes('api.picktow.com') || apiURL.includes('homolog.api.picktow.com')) {
    return '/api-picktow';
  }

  return apiURL;
};

const api = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: getApiBaseURL(),
});

api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    const originalRequest = error.config;
    if (error?.response?.data?.exp === 'token expired') {
      const { refresh_token } = store.getState().auth.authorization;

      return new Promise((resolve, reject) => {
        api
          .post('/identity-providers/BACKOFFICE/authentications:refresh', {
            refresh_token,
          })
          .then(({ data }) => {
            store.dispatch(refreshTokenSuccess(data));
            originalRequest.headers.Authorization = `Bearer ${data.id_token}`;
            resolve(axios(originalRequest));
          })
          .catch(err => {
            reject(err);
            store.dispatch(logout());
          });
      });
    }
    return Promise.reject(error);
  },
);

api.interceptors.request.use(config => {
  const token = store.getState().auth.authorization.id_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export function loginApi(username, password) {
  return api.post('/identity-providers/BACKOFFICE/authentications', {
    username,
    password,
  });
}

export function setAuthorizationHeader(token) {
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}

export const forgotApi = username => {
  return api.post('/identity-providers/BACKOFFICE/authentications:forgottenPassword', { username });
};

export const resetPasswordApi = (username, password, resetCode) => {
  return api.post('/identity-providers/BACKOFFICE/authentications:resetPassword', {
    username,
    new_password: password,
    reset_code: resetCode,
  });
};

export const changePasswordApi = (username, currentPassword, newPassword) => {
  return api.post(`/identity-providers/BACKOFFICE/authentications/${username}:changePassword`, {
    old_password: currentPassword,
    new_password: newPassword,
  });
};

export const uploadFiles = bodyFormData => {
  return api.post('/files:upload', bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const documentsAssociation = (associationId, data) => {
  // types
  //   BANK_DATA,  => url deverá ser passada via value
  //   CPF,  => url deverá ser passada via value
  //   CNPJ,  => url deverá ser passada via value
  //   STATE_SUBSCRIPTION,  => url deverá ser passada via value
  //   PROOF_OF_CNPJ => url deverá ser passada no parâmetro path
  //   SOCIAL_CONTRACT => url deverá ser passada no parâmetro path
  //   PROOF_OF_RESIDENCE => url deverá ser passada no parâmetro path
  return api.post(`/documents/${associationId}`, data);
};

export const createAssociation = data => {
  return api.post('/associations', data);
};

export const loadAssociations = (associationId = '') => {
  return api.get(`/associations/${associationId}`);
};

export const customerLoad = (association_id, data) => {
  return api.post('/spreadsheets/customer:upload', {
    association_id,
    data,
  });
};

export const deleteQuiz = quizId => {
  return api.delete(`quizzes/${quizId}`);
};

export const restoreQuiz = quizId => {
  return api.post(`quizzes/${quizId}:undelete`);
};

export const inactiveQuiz = quizId => {
  return api.post(`quizzes/${quizId}:inactive`);
};

export const activeQuiz = quizId => {
  return api.post(`quizzes/${quizId}:active`);
};

export const createQuiz = (code, association_id, service_id, form) => {
  return api.post('quizzes', {
    code,
    association_id,
    service_id,
    form,
  });
};

export const changeQuiz = (quizId, code, association_id, service_id, active, form) => {
  return api.put(`quizzes/${quizId}`, {
    code,
    association_id,
    service_id,
    active,
    form,
  });
};

export const loadQuiz = quizId => {
  return api.get(`quizzes/${quizId}`);
};

export const loadQuizFromAssociation = associationId => {
  return api.get(`quizzes/associations/${associationId}`);
};

export const loadBenefits = (associationId, serviceId) => {
  return api.get(`benefits/associations/${associationId}/services/${serviceId}`);
};

export const createBenefits = data => {
  return api.post('benefits', data);
};

export const changeBenefits = (id, data) => {
  return api.put(`benefits/${id}`, data);
};

export const deleteBenefits = id => {
  return api.delete(`benefits/${id}`);
};

export const loadPeriods = () => {
  return api.get(`periods`);
};

export const loadServices = () => {
  return api.get('services');
};

export const loadContractorRating = (contractorId, from, to) => {
  return api.get(appendQueryParamsTo(`contractors/survey/${contractorId}`, { from, to }));
};

export const loadVehicleServicesApi = vehicleId => {
  return api.get(`associated/services/contractors/${vehicleId}`);
};

export const loadCategoriesByServices = serviceId => {
  return api.get(`services-categories/services/${serviceId}`);
};

export const loadPricingByAssociationAndServiceCategory = (associationId, serviceCategoryId) => {
  return api.get('pricing-association-tables/pricing-associations', {
    params: {
      associationId,
      serviceCategoryId,
    },
  });
};

export const createAssociationPricingApi = data => {
  return api.post('pricing-association-tables', data);
};

export const updateAssociationPricingApi = (tableId, data) => {
  return api.put(`pricing-association-tables/${tableId}`, data);
};

export const deleteAssociationPricingApi = tableId => {
  return api.delete(`pricing-association-tables/${tableId}`);
};

export const loadPricingByContractorAndServiceCategory = (contractorId, serviceCategoryId) => {
  // Contrato espelhado da API de associação para prestador.
  return api.get('pricing-contractor-tables/pricing-contractors', {
    params: { contractorId, serviceCategoryId },
  });
};

export const createContractorPricingApi = data => {
  return api.post('pricing-contractor-tables', data);
};

export const listPricingTemplatesApi = (prestadorId) => {
  const simovsatApi = getSimovsatApiInstance();
  const params = prestadorId ? { prestador_id: prestadorId } : undefined;
  return simovsatApi.get(getSimovsatEndpoint('/pricing-templates'), { params });
};

export const getPricingTemplateByIdApi = templateId => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.get(getSimovsatEndpoint(`/pricing-templates/${templateId}`));
};

export const createPricingTemplateApi = data => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.post(getSimovsatEndpoint('/pricing-templates'), data);
};

export const updatePricingTemplateApi = (templateId, data) => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.put(getSimovsatEndpoint(`/pricing-templates/${templateId}`), data);
};

export const applyPricingTemplateApi = (templateId, data) => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.post(getSimovsatEndpoint(`/pricing-templates/${templateId}/apply`), data);
};


const getContractorPricingApiInstance = () => {
  const baseURL = process.env.REACT_APP_CONTRACTOR_PRICING_API_URL || 'https://new-homolog.picktow.com';
  return axios.create({ baseURL });
};

export const createContractorPricingV1Api = data => {
  return getContractorPricingApiInstance().post('/api/v1/contractor-pricing', data);
};

export const getAssociationUseCompanyTableApi = associationId => {
  return getContractorPricingApiInstance().get(`/api/v1/associations/${associationId}/use-company-table`);
};

export const setAssociationUseCompanyTableApi = (associationId, useCompanyTable) => {
  return getContractorPricingApiInstance().patch(
    `/api/v1/associations/${associationId}/use-company-table`,
    { use_company_table: useCompanyTable },
  );
};

export function loadContractors() {
  return api.get('contractors');
}

export function loadContractor(contractorId) {
  return api.get(`contractors/${contractorId}:fetchDetails`);
}

export function editContractorApi(contractorId, contractor) {
  return api.put(`contractors/${contractorId}/management`, contractor);
}

export function createContractorApi(data) {
  return api.post('contractors', data);
}

export function deleteContractorApi(contractorId) {
  const baseURL = `${process.env.REACT_APP_CONTRACTOR_PRICING_API_URL}/api`;
  return axios.delete(`${baseURL}/v1/contractors/${contractorId}`, {
    headers: { accept: 'application/json' },
  });
}

export const updateEmployeeApi = (employeeId, data) => {
  return api.put(`employees/${employeeId}`, data);
};

export const createEmployeeApi = data => {
  return api.post('employees', data);
};

export const deleteEmployeeApi = employeeId => {
  return api.delete(`employees/${employeeId}`);
};

export const changeContractorApi = data => {
  return api.put('identities/CONTRACTOR:admin', data);
};

export const signUpContractorApi = data => {
  return api.post('identities/CONTRACTOR:admin', data);
};

export function loadVehicles(contractorId) {
  return api.get(`contractor/vehicle/organization/contractors/${contractorId}:getAll`);
}

export function loadEmployees(organizationId) {
  return api.get(`employees/organizations/${organizationId}`);
}

export function loadDocumentsApi(contractorId) {
  return api.get(`documents/${contractorId}:findImages`);
}

export function approveContractor(identityId, approved = true, status_reason = '') {
  return api.patch(`documents/${identityId}/verified`, { approved, status_reason });
}

export function loadOrdersFromContractor(contractorId, statuses) {
  return api.get(appendQueryParamsTo(`orders/contractor/${contractorId}`, { status: statuses }));
}

export function loadOpportunities(status, from, to, vin) {
  if (Array.isArray(status)) {
    return loadOpportunitiesInSeveralStatuses(status, from, to, vin);
  }
  const source = axios.CancelToken.source();
  const promise = api.get(
    `orders?status=${status}${from && to ? `&from=${from}&to=${to}` : ''}${vin ? `&vin=${vin}` : ''}`,
    {
      cancelToken: source.token,
    },
  );
  promise.cancel = () => source.cancel();
  return promise;
}

function loadOpportunitiesInSeveralStatuses(statuses = [], from, to, vin) {
  const source = axios.CancelToken.source();

  const promise = api.get(appendQueryParamsTo('orders', { status: statuses, from, to, vin }), {
    cancelToken: source.token,
  });
  promise.cancel = () => source.cancel();
  return promise;
}

export function uploadFile(bodyFormData) {
  return api.post('/files:upload', bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export const uploadPublicFilesApi = bodyFormData => {
  return api.post('/assets/files:upload', bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const changePictureContractorAdminApi = (username, data) => {
  return api.patch(`/identities/CONTRACTOR/${username}:changePictureAdmin`, data);
};

export function updateDocument(documentId, document) {
  return api.put(`documents/${documentId}`, document);
}

export function createDocument(identityId, document) {
  return api.post(`documents/${identityId}`, document);
}

export function sendReviewDocumentsEmail(identityId, data) {
  return api.post(`documents/${identityId}/review`, data);
}

export function loadAllocationContractors(service_id, customer_vehicle_type) {
  const source = axios.CancelToken.source();
  const promise = api.get(
    `allocations/contractors:listContractorsAbleTo?serviceId=${service_id}&customerVehicleType=${customer_vehicle_type}`,
    {
      cancelToken: source.token,
    },
  );
  promise.cancel = () => source.cancel();
  return promise;
}

export function allocateOpportunityForContractor(orderId, contractorId) {
  return api.post(`orders/${orderId}:manualAllocation`, { contractor_id: contractorId });
}

export function loadCustomerApi(customerId) {
  return api.get(`customers/${customerId}:fetchDetails`);
}

export function loadCancelReasons() {
  return api.get('cancellation-reasons');
}

export function forceCancelOrder(orderId, reason, penalty_charge) {
  return api.post(`orders/${orderId}:forceCancel`, { reason, penalty_charge });
}

export function resetCustomer(username, confirmation_type) {
  return api.post(`identities/CUSTOMER/${username}:resetAlias`, { confirmation_type });
}

export function resetContractor(username, confirmation_type) {
  return api.post(`identities/CONTRACTOR/${username}:resetAlias`, { confirmation_type });
}

export function deleteCustomer(username) {
  return api.post(`identities/CUSTOMER/${username}:delete`);
}

export function deleteContractor(username) {
  return api.delete(`identities/CONTRACTOR/${username}`);
}

export function loadQuizAnswers(orderId) {
  return api.get(`quiz-answers/orders/${orderId}`);
}

export function loadOrderDocuments(orderId) {
  return api.get(`documents/orders/${orderId}`);
}

export function loadAllocationCancelReasons() {
  return api.get('cancel_reasons');
}

export function forceCancelAllocation(contractorId, cancelReasonId) {
  return api.post(`invites/contractors/${contractorId}/cancel_reasons/${cancelReasonId}`);
}

export function loadOrderDetails(orderId, type) {
  return api.get(`order-details/orders/${orderId}`, { params: { type } });
}

export const deleteVehicleAPI = vehicleId => {
  return api.delete(`contractor/vehicle/${vehicleId}`);
};

export const getAllBanksApi = () => {
  return api.get('/integrations/bank-list');
};

export function loadOrderCosts(orderId) {
  return api.get(`cost-parameters/orders/${orderId}`);
}

export const sendToManualAllocationAPI = (orderId, data) => {
  return api.post(`cancellations/orders/${orderId}`, data);
};

export const getOrderHistoryApi = orderId => {
  return api.get(`orders/history/orders/${orderId}`);
};

export const getAllocationsHistoryApi = orderId => {
  return api.get(`allocations/history/orders/${orderId}`);
};

export const getPaymentHistoryApi = orderId => {
  return api.get(`payments/history/orders/${orderId}`);
};

export const getInvitesApi = orderId => {
  return api.get(`invites/orders/${orderId}`);
};

export const deleteCustomerVehicleApi = vehicleId => {
  return api.delete(`customers/vehicles/${vehicleId}`);
};

export const getVehicleBySocialSecurityApi = socialSecurity => {
  return api.get(`customers/vehicles/social-security/${socialSecurity}`);
};

export const getQuizByAssociationServiceApi = (associationId, serviceId) => {
  return api.get(`/quizzes/associations/${associationId}/services/${serviceId}`);
};

export const getQuizByServiceApi = serviceId => {
  return api.get(`/quizzes/services/${serviceId}`);
};

export const validateRegionsServiceApi = data => {
  return api.post('service-regions/within', data);
};

export const getTimeZoneByGoogleApi = (API_KEY, latitude, longitude) => {
  return axios.get(
    `https://maps.googleapis.com/maps/api/timezone/json?location=${latitude},${longitude}&timestamp=1458000000&key=${API_KEY}`,
  );
};

export const maplinkTripApi = data => {
  return api.post('trip', data);
};

export const mapCalculatorApi = data => {
  return api.post('customers/calculation', data);
};

export const getAllPaymentMethodsApi = () => {
  return api.get('payment-methods');
};

export const createOrderApi = data => {
  return api.post('orders', data);
};

export const setOrderCreatorEmailApi = (orderId, email) => {
  const baseURL = `${process.env.REACT_APP_CONTRACTOR_PRICING_API_URL}/api/v1`;
  return axios.patch(`${baseURL}/orders/${orderId}/creator-email`, { email }, {
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
  });
};

export const getOrderCreatorEmailApi = orderId => {
  const baseURL = `${process.env.REACT_APP_CONTRACTOR_PRICING_API_URL}/api/v1`;
  return axios.get(`${baseURL}/orders/${orderId}/creator-email`, {
    headers: { accept: 'application/json' },
  });
};

export const loadContractorsByLatLng = (latitude, longitude) => {
  return api.get(`coordinates/contractors?lat=${latitude}&lng=${longitude}&radiusKm=2500`);
};

export const loadContractorsInfoByIdAndStatus = (contractorId, { status }) => {
  return api.get(`orders/contractor/${contractorId}?status=${status}`);
};

export function addNoteByOrderId(orderId, note) {
  return api.put(`orders/${orderId}:addAnnotation`, { note });
}
export const uploadFileToOrderAPI = data => {
  return api.put('documents/orders/photo-file:upload', data);
};

export const loadVehicleBenefits = (associationId, serviceId, socialSecurity, vehicleId) => {
  return api.get(
    `benefits/associations/${associationId}/services/${serviceId}/social-security/${socialSecurity}/vehicle/${vehicleId}`,
  );
};

export const loadVehicleBenefitQuota = (vehicleId, benefitId) => {
  return api.get(`customers/vehicles/${vehicleId}/benefits/${benefitId}:benefitsQuotaUsage`);
};

export const getVehicleTypesApi = () => {
  return api.get('/customers/vehicles/types');
};

export const getFipeManufacturersByCategoryApi = categoryName => {
  return api.get(`/integrations/FIPE/categories/${categoryName}/manufacturers`);
};

export const getFipeVehiclesByManufacturersAndCategoryApi = (categoryName, manufacturerId) => {
  return api.get(`/integrations/FIPE/categories/${categoryName}/manufacturers/${manufacturerId}/vehicles`);
};

export const getFipeModelsByVehiclesAndManufacturersAndCategoryApi = (categoryName, manufacturerId, vehicleId) => {
  return api.get(
    `/integrations/FIPE/categories/${categoryName}/manufacturers/${manufacturerId}/vehicles/${vehicleId}/models`,
  );
};

export const loadAssociationBalance = associationId => {
  return api.get(`associations/${associationId}:getAccountStatement`);
};

export const createNewUserApi = data => {
  return api.post('identities/CUSTOMER:admin', data);
};

export const createCustomerVehicleApi = data => {
  return api.post('customers/vehicles', data);
};

export const createCustomerVehicleBenefitsApi = (vehicleId, data) => {
  return api.post(`customers/vehicles/${vehicleId}/benefits`, data);
};

export const changeVehicleApi = (vehicleId, data) => {
  return api.put(`customers/vehicles/${vehicleId}`, data);
};

export const loadUsersB2BApi = () => {
  return api.get('identities/BACKOFFICE?role=B2B');
};

export const createUserB2BApi = data => {
  return api.post('identities/BACKOFFICE:admin', data);
};

export const changeUserB2BApi = data => {
  return api.put('identities/BACKOFFICE:admin', data);
};

export const enabledUserB2BApi = username => {
  return api.post(`identities/BACKOFFICE/${username}:enable`);
};

export const deleteUserB2BApi = username => {
  return api.delete(`identities/BACKOFFICE/${username}`);
};

export const inactivateAssociationVehiclesApi = associationId => {
  return api.post(`associations/${associationId}/vehicles:inactivate`);
};

export const changeCustomerApi = data => {
  return api.put('identities/CUSTOMER:admin', data);
};

export const disableCustomerApi = customerId => {
  return api.delete(`identities/CUSTOMER/${customerId}`);
};

export const enableCustomerApi = customerId => {
  return api.post(`identities/CUSTOMER/${customerId}:enable`);
};

export const getPaymentDetailsApi = orderId => {
  return api.get(`payments/details/orders/${orderId}`);
};

export const updateAssociation = (associationId, data) => {
  return api.put(`/associations/${associationId}`, data);
};

export function loadCustomerByLicensePlateApi(licensePlate) {
  return api.get(`customers/vehicles/license-plate/${licensePlate}`);
}

export const getContractorApi = username => {
  return api.get(`identities/CONTRACTOR/${username}`);
};

export const getImageFileApi = file => {
  return api.get(`files:download`, { responseType: 'blob', params: { url: file } });
};

export const getContractorsAllocatedApi = () => {
  return api.get('allocations/contractors');
};

export const createContractorAllocatedApi = contractorId => {
  return api.post(`allocations/contractors/${contractorId}`);
};

export const deleteContractorAllocatedApi = contractorId => {
  return api.delete(`allocations/contractors/${contractorId}`);
};

export const sendPushApi = data => {
  return api.post('notifications/push', data);
};

export const getContractorVehicleTypesApi = () => {
  return api.get('/contractor/vehicle/types');
};

export const getVehicleServicesByTypeIDApi = typeId => {
  return api.get(`/contractor/vehicle/services/${typeId}`);
};

export const createVehicleApi = data => {
  return api.post('/contractor/vehicle', data);
};

export const updateVehicleApi = (vehicleId, data) => {
  return api.put(`/contractor/vehicle/${vehicleId}`, data);
};

export const syncHinovaApi = data => {
  return api.post('/webhooks/hinova', data);
};

export const loadSimovsatVehicles = (kmMin, kmMax, placa) => {
  // Sempre usa URL relativa quando a URL base for localhost:5000
  // O proxy em setupProxy.js redireciona /api para http://localhost:5000
  // Isso funciona tanto em desenvolvimento quanto quando rodando o servidor de dev com env de produção
  const simovsatBaseURL = process.env.REACT_APP_SIMOVSAT_API_URL || process.env.SIMOVSAT_API_URL || '';
  const isLocalhost5000 = simovsatBaseURL.includes('localhost:5000');

  // Se for localhost:5000, usa URL relativa para que o proxy intercepte
  // Caso contrário, usa a URL completa
  const baseURL = isLocalhost5000 ? '/api' : simovsatBaseURL;

  const simovsatApi = axios.create({
    baseURL,
  });

  const params = new URLSearchParams();
  if (kmMin) params.append('km_min', kmMin);
  if (kmMax) params.append('km_max', kmMax);
  if (placa) params.append('placa', placa);

  return simovsatApi.get(`api/simovsat/vehicles?${params.toString()}`);
};

export const isSimovsatAvailable = () => {
  const simovsatBaseURL = process.env.REACT_APP_SIMOVSAT_API_URL || process.env.SIMOVSAT_API_URL || '';
  if (!simovsatBaseURL) return false;
  const isLocalhost5000 = simovsatBaseURL.includes('localhost:5000');
  return !isLocalhost5000 || process.env.NODE_ENV === 'development';
};

const getSimovsatApiInstance = () => {
  const simovsatBaseURL = process.env.REACT_APP_SIMOVSAT_API_URL || process.env.SIMOVSAT_API_URL || '';
  if (!simovsatBaseURL) {
    throw new Error('REACT_APP_SIMOVSAT_API_URL não configurada');
  }
  const isLocalhost5000 = simovsatBaseURL.includes('localhost:5000');
  if (isLocalhost5000 && process.env.NODE_ENV !== 'development') {
    throw new Error('Simovsat não disponível neste ambiente');
  }
  const baseURL = isLocalhost5000 ? '/api' : simovsatBaseURL;

  return axios.create({
    baseURL,
  });
};

const getSimovsatEndpoint = (endpoint) => {
  const simovsatBaseURL = process.env.REACT_APP_SIMOVSAT_API_URL || process.env.SIMOVSAT_API_URL || '';
  const isLocalhost5000 = simovsatBaseURL.includes('localhost:5000');

  // Se for localhost:5000, a baseURL já é '/api', então o endpoint deve ser '/v1/...'
  // Se for URL completa, o endpoint deve incluir '/api/v1/...'
  if (isLocalhost5000) {
    return `/v1${endpoint}`;
  }
  return `/api/v1${endpoint}`;
};

export const listSimovsatVehicles = (page = 1, perPage = 20, filters = {}) => {
  const simovsatApi = getSimovsatApiInstance();
  const params = new URLSearchParams();

  if (filters.license_plate) {
    params.append('placa', filters.license_plate.replace(/[^a-zA-Z0-9]/g, ''));
  }
  if (filters.km_min) {
    params.append('km_min', filters.km_min);
  }
  if (filters.km_max) {
    params.append('km_max', filters.km_max);
  }
  if (filters.status) {
    params.append('status', filters.status);
  }

  return simovsatApi.get(`api/simovsat/vehicles?${params.toString()}`);
};

export const getSimovsatVehicleById = vehicleId => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.get(getSimovsatEndpoint(`/vehicles/${vehicleId}`));
};

export const listSimovsatOrganizations = (page = 1, perPage = 20, filters = {}) => {
  const simovsatApi = getSimovsatApiInstance();
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
  });

  if (filters.email) {
    params.append('email', filters.email);
  }
  if (filters.company_name) {
    params.append('company_name', filters.company_name);
  }

  return simovsatApi.get(`${getSimovsatEndpoint('/organizations')}?${params.toString()}`);
};

export const updateSimovsatVehicle = (vehicleId, vehicleData) => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.put(getSimovsatEndpoint(`/vehicles/${vehicleId}`), vehicleData);
};

export const deleteSimovsatVehicle = vehicleId => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.delete(getSimovsatEndpoint(`/vehicles/${vehicleId}`));
};

export const createSimovsatVehicle = vehicleData => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.post(getSimovsatEndpoint('/vehicles'), vehicleData);
};

export const importVehiclesFromHinova = payload => {
  const simovsatApi = getSimovsatApiInstance();
  return simovsatApi.post('/api/vehicles/import', payload);
};

export const loadSimovsatCharts = () => {
  const baseURL = process.env.REACT_APP_CONTRACTOR_PRICING_API_URL || 'http://164.90.145.1';
  return axios.get(`${baseURL}/api/v1/charts/simovsat`, {
    headers: { accept: 'application/json' },
  });
};

// ==================== HINOVA API ====================

const getHinovaProxyApiInstance = () => {
  const simovsatBaseURL = process.env.REACT_APP_SIMOVSAT_API_URL || process.env.SIMOVSAT_API_URL || '';
  if (!simovsatBaseURL) {
    throw new Error('REACT_APP_SIMOVSAT_API_URL não configurada');
  }

  const isLocalhost5000 = simovsatBaseURL.includes('localhost:5000');
  if (isLocalhost5000 && process.env.NODE_ENV !== 'development') {
    throw new Error('Simovsat não disponível neste ambiente');
  }

  return axios.create({
    baseURL: isLocalhost5000 ? '/api' : simovsatBaseURL,
  });
};

const getHinovaProxyEndpoint = endpoint => getSimovsatEndpoint(`/hinova${endpoint}`);

const unwrapHinovaProxyData = data => {
  if (data?.data !== undefined) {
    return data.data;
  }

  return data;
};

const normalizeHinovaProxyResponse = response => ({
  ...response,
  data: unwrapHinovaProxyData(response.data),
  rawData: response.data,
});

// Armazena token e cookie em memória
let hinovaToken = null;
let hinovaCookie = null;
let isAuthenticating = false;
let authenticationPromise = null;

const getHinovaBaseURL = () => {
  const hinovaBaseURL = process.env.REACT_APP_HINOVA_API_URL || '';
  const useHinovaProxy = process.env.REACT_APP_USE_HINOVA_PROXY === 'true';

  if (hinovaBaseURL.startsWith('/')) {
    return hinovaBaseURL;
  }

  if (useHinovaProxy && hinovaBaseURL.includes('api.hinova.com.br')) {
    return '/api-hinova';
  }

  return hinovaBaseURL;
};

// Cria instância única do axios para Hinova
const createHinovaApiInstance = () => {
  const hinovaApi = axios.create({
    // Permite usar proxy relativo em qualquer ambiente.
    baseURL: getHinovaBaseURL(),
    withCredentials: false,
  });

  // Interceptor de requisição - adiciona token e cookie
  hinovaApi.interceptors.request.use(
    async config => {
      // Se não tem token e não está autenticando, tenta autenticar primeiro
      if (!hinovaToken && !isAuthenticating && !config.url?.includes('/usuario/autenticar')) {
        const initialBearerToken = process.env.REACT_APP_HINOVA_BEARER_TOKEN || '';
        if (!initialBearerToken) {
          // Se não tem Bearer Token inicial, tenta autenticar
          try {
            isAuthenticating = true;
            authenticationPromise = hinovaAuthenticateInternal();
            await authenticationPromise;
          } catch (authError) {
            console.warn('Falha na autenticação automática Hinova:', authError);
            // Continua mesmo se falhar, pode ser que o Bearer Token inicial funcione
          } finally {
            isAuthenticating = false;
            authenticationPromise = null;
          }
        }
      }

      // Se está autenticando, aguarda a conclusão
      if (isAuthenticating && authenticationPromise && !config.url?.includes('/usuario/autenticar')) {
        try {
          await authenticationPromise;
        } catch (authError) {
          console.warn('Erro ao aguardar autenticação:', authError);
        }
      }

      // Prioriza o token_usuario obtido na autenticação, senão usa o Bearer Token inicial
      const bearerToken = hinovaToken || process.env.REACT_APP_HINOVA_BEARER_TOKEN || '';
      const phpsessid = hinovaCookie || process.env.REACT_APP_HINOVA_PHPSESSID || '';

      if (bearerToken) {
        config.headers.Authorization = `Bearer ${bearerToken}`;
      }
      if (phpsessid) {
        config.headers.Cookie = `PHPSESSID=${phpsessid}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Interceptor de resposta - trata erros 401 e autentica automaticamente
  hinovaApi.interceptors.response.use(
    response => {
      return response;
    },
    async error => {
      const originalRequest = error.config;

      // Se for erro 401 e não for uma requisição de autenticação, tenta autenticar
      if (error?.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/usuario/autenticar')) {
        originalRequest._retry = true;

        // Se já está autenticando, aguarda a conclusão
        if (isAuthenticating && authenticationPromise) {
          try {
            await authenticationPromise;
          } catch (authError) {
            console.warn('Erro ao aguardar autenticação em andamento:', authError);
            // Continua para tentar autenticar novamente
          }
        }

        // Tenta autenticar para obter novo token
        if (!isAuthenticating) {
          isAuthenticating = true;
          authenticationPromise = hinovaAuthenticateInternal();

          try {
            await authenticationPromise;
            console.log('Autenticação Hinova bem-sucedida após 401');
          } catch (authError) {
            console.error('Falha na autenticação Hinova após 401:', authError);
            isAuthenticating = false;
            authenticationPromise = null;
            // Se falhar a autenticação, rejeita o erro original
            return Promise.reject(error);
          }

          isAuthenticating = false;
          authenticationPromise = null;
        }

        // Atualiza headers da requisição original com novo token
        const bearerToken = hinovaToken || process.env.REACT_APP_HINOVA_BEARER_TOKEN || '';
        const phpsessid = hinovaCookie || process.env.REACT_APP_HINOVA_PHPSESSID || '';

        if (bearerToken) {
          originalRequest.headers.Authorization = `Bearer ${bearerToken}`;
        } else {
          // Se ainda não tem token após autenticação, remove header Authorization
          delete originalRequest.headers.Authorization;
        }

        if (phpsessid) {
          originalRequest.headers.Cookie = `PHPSESSID=${phpsessid}`;
        } else {
          delete originalRequest.headers.Cookie;
        }

        // Repete a requisição original
        return hinovaApi(originalRequest);
      }

      return Promise.reject(error);
    }
  );

  return hinovaApi;
};

// Instância única
let hinovaApiInstance = null;

const getHinovaApiInstance = () => {
  if (!hinovaApiInstance) {
    hinovaApiInstance = createHinovaApiInstance();
  }
  return hinovaApiInstance;
};

// Autenticação interna (usada pelo interceptor)
const hinovaAuthenticateInternal = async () => {
  // A API requer o Bearer Token inicial no header de autenticação
  const initialBearerToken = process.env.REACT_APP_HINOVA_BEARER_TOKEN || '';
  const initialPhpsessid = process.env.REACT_APP_HINOVA_PHPSESSID || '';

  const authApi = axios.create({
    baseURL: getHinovaBaseURL(),
    withCredentials: false,
    headers: {
      Authorization: initialBearerToken ? `Bearer ${initialBearerToken}` : undefined,
      Cookie: initialPhpsessid ? `PHPSESSID=${initialPhpsessid}` : undefined,
    },
  });

  // Tenta diferentes formatos de body
  const username = process.env.REACT_APP_HINOVA_USER || '';
  const password = process.env.REACT_APP_HINOVA_PASSWORD || '';

  if (!username || !password) {
    throw new Error('Credenciais Hinova não configuradas. Verifique REACT_APP_HINOVA_USER e REACT_APP_HINOVA_PASSWORD no arquivo .env');
  }

  if (!initialBearerToken) {
    throw new Error('Bearer Token inicial não configurado. Verifique REACT_APP_HINOVA_BEARER_TOKEN no arquivo .env');
  }

  let response;
  let lastError;

  // Tenta diferentes formatos de body que a API pode esperar
  const formats = [
    { usuario: username, senha: password },
    { login: username, password: password },
    { user: username, pass: password },
    { username: username, password: password },
  ];

  for (const bodyFormat of formats) {
    try {
      response = await authApi.post('/usuario/autenticar', bodyFormat);
      // Se chegou aqui, a requisição foi bem-sucedida
      break;
    } catch (err) {
      lastError = err;
      // Se não for erro 400/401, pode ser outro problema, então continua tentando
      if (err?.response?.status !== 400 && err?.response?.status !== 401) {
        continue;
      }
    }
  }

  // Se nenhum formato funcionou, lança o último erro
  if (!response) {
    throw lastError || new Error('Falha na autenticação Hinova');
  }

  // Extrai token_usuario da resposta (a API retorna token_usuario, não token)
  if (response.data?.token_usuario) {
    hinovaToken = response.data.token_usuario;
  } else if (response.data?.token) {
    hinovaToken = response.data.token;
  } else if (response.data?.data?.token_usuario) {
    hinovaToken = response.data.data.token_usuario;
  } else if (response.data?.data?.token) {
    hinovaToken = response.data.data.token;
  }

  // Extrai cookie dos headers
  if (response.headers['set-cookie']) {
    const cookieHeader = response.headers['set-cookie'].find(c => c.startsWith('PHPSESSID='));
    if (cookieHeader) {
      hinovaCookie = cookieHeader.split('=')[1].split(';')[0];
    }
  }

  return response;
};

// Autenticação pública (pode ser chamada manualmente)
export const hinovaAuthenticate = async (username, password) => {
  return {
    status: 200,
    data: null,
    rawData: null,
  };
};

// Listar produtos
export const listHinovaProducts = (codigoRegional, codigoCooperativa, codigoTipoVeiculo, valorFipe, cilindrada) => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(
    getHinovaProxyEndpoint(
      `/produtos/${codigoRegional}/${codigoCooperativa}/${codigoTipoVeiculo}/${valorFipe}/${cilindrada}`,
    ),
  ).then(normalizeHinovaProxyResponse);
};

// Buscar associado por CPF
export const searchHinovaAssociado = cpf => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint(`/associados/${cpf}`)).then(normalizeHinovaProxyResponse);
};

// Buscar histórico do associado
export const searchHinovaHistorico = cpf => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint(`/associados/${cpf}/historico`)).then(normalizeHinovaProxyResponse);
};

// Listar departamentos ativos
export const listHinovaDepartamentos = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/listar/departamento/ativo');
};

// Listar status de atendimento ativos
export const listHinovaStatusAtendimento = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/listar/status-atendimento/ativo');
};

// Listar tipos de atendimento ativos
export const listHinovaTipoAtendimento = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/listar/tipo-atendimento/ativo');
};

// Listar cooperativas ativas
export const listHinovaCooperativas = () => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint('/cooperativas/ativas')).then(normalizeHinovaProxyResponse);
};

// Listar benefícios por situação ativos
export const listHinovaBeneficios = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/listar/beneficio-por-situacao/ativo');
};

// Listar boletos
export const listHinovaBoletos = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/listar/boleto');
};

// Listar boletos de associado e veículo
export const listHinovaBoletoAssociadoVeiculo = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/listar/boleto-associado-veiculo');
};

// Listar eventos de veículo por placa
export const listHinovaEventosVeiculo = placa => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get(`/listar/evento-veiculo/${placa}`);
};

// Buscar rateio médio
export const searchHinovaRateioMedio = () => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get('/buscar/rateio-medio');
};

// Buscar veículo por placa
export const searchHinovaVeiculo = placa => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint(`/veiculos/${placa}`)).then(normalizeHinovaProxyResponse);
};

// Listar veículos
// codigo_situacao: código da situação (obrigatório) - valores comuns: '1' (ativo), '2' (inativo), '3' (todos)
// inicio_paginacao: início da paginação (opcional, padrão: 0)
// quantidade_por_pagina: quantidade por página (opcional, padrão: 5000)
// data_contrato: filtra por data exata do contrato (opcional, formato YYYY-MM-DD ou dd/mm/yyyy)
export const listHinovaVeiculos = (codigoSituacao = '1', inicioPaginacao = 0, quantidadePorPagina = 999, dataContrato = undefined) => {
  const hinovaApi = getHinovaProxyApiInstance();
  const payload = {
    codigo_situacao: codigoSituacao,
    inicio_paginacao: inicioPaginacao.toString(),
    quantidade_por_pagina: quantidadePorPagina.toString(),
  };
  if (dataContrato) {
    payload.data_contrato = dataContrato;
  }
  return hinovaApi.post(getHinovaProxyEndpoint('/veiculos/listar'), payload).then(normalizeHinovaProxyResponse);
};

// Listar tipos de veículo
// situacao: 'ativo', 'inativo' ou 'todos'
export const listHinovaTiposVeiculo = (situacao = 'ativo') => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint(`/tipos-veiculo/${situacao}`)).then(normalizeHinovaProxyResponse);
};

// Buscar voluntário por ID
export const searchHinovaVoluntario = id => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get(`/buscar/voluntario/${id}`);
};

// Listar regionais ativas
export const listHinovaRegionais = () => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint('/regionais/ativas')).then(normalizeHinovaProxyResponse);
};

// Listar situações ativas
export const listHinovaSituacoes = () => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.get(getHinovaProxyEndpoint('/situacoes/ativas')).then(normalizeHinovaProxyResponse);
};

// Listar associados
export const listHinovaAssociados = () => {
  const hinovaApi = getHinovaProxyApiInstance();
  return hinovaApi.post(getHinovaProxyEndpoint('/associados'), {
    codigo_situacao: '1',
    inicio_paginacao: 0,
    fim_paginacao: 999,
    quantidade_por_pagina: 999,
  }).then(normalizeHinovaProxyResponse);
};

// Buscar boleto por código
export const getHinovaBoleto = codigo => {
  const hinovaApi = getHinovaApiInstance();
  return hinovaApi.get(`/buscar/boleto/${codigo}`);
};

// --- Hinova Filter Presets (CRUD no backend picktow) ---
export const createHinovaFilterPreset = data => api.post('/hinova-filter-presets', data);

export const listHinovaFilterPresets = () => api.get('/hinova-filter-presets');

export const getHinovaFilterPreset = id => api.get(`/hinova-filter-presets/${id}`);

export const updateHinovaFilterPreset = (id, data) => api.put(`/hinova-filter-presets/${id}`, data);

export const deleteHinovaFilterPreset = id => api.delete(`/hinova-filter-presets/${id}`);

export default api;
